/**
 * Filtering and counts for the landing page's GMP section (components/gmp/GmpSection.jsx).
 * Rows come from utils/gmpDataMapper.js mapGmpItem. The table and every count
 * use the same rules, so each number matches what clicking it would show.
 */

// Preferred order for status tabs; anything else the source returns is appended
export const STATUS_ORDER = ['open', 'upcoming', 'closed', 'allotted', 'listed'];

const BOARD_LABELS = { ALL: 'IPOs', MAINBOARD: 'Mainboard IPOs', SME: 'SME IPOs' };

/**
 * @param {object} row - mapGmpItem output
 * @param {object} filters - { query, board: 'ALL' | 'MAINBOARD' | 'SME', status: 'all' | statusKey, gmpOnly }
 */
export const matchesGmpFilters = (row, { query = '', board = 'ALL', status = 'all', gmpOnly = false } = {}) => {
  const needle = query.trim().toLowerCase();
  if (needle && !row.companyName.toLowerCase().includes(needle)) return false;
  if (board === 'SME' && !row.isSme) return false;
  if (board === 'MAINBOARD' && row.isSme) return false;
  if (status !== 'all' && row.statusKey !== status) return false;
  if (gmpOnly && !row.gmpDisplay) return false;
  return true;
};

export const filterGmpRows = (rows, filters) => rows.filter((row) => matchesGmpFilters(row, filters));

/**
 * Status chips with counts under every other active filter (board, search,
 * GMP-only). Every status the feed has keeps its chip, at 0 when nothing matches,
 * so the row doesn't jump around as filters change.
 * Returns { allCount, tabs: [{ key, label, count }] }.
 */
export const buildStatusTabs = (rows, filters = {}) => {
  const base = filterGmpRows(rows, { ...filters, status: 'all' });
  const tabs = new Map();
  rows.forEach((row) => {
    if (!row.status || tabs.has(row.statusKey)) return;
    tabs.set(row.statusKey, { key: row.statusKey, label: row.status, count: 0 });
  });
  base.forEach((row) => {
    const tab = tabs.get(row.statusKey);
    if (tab) tab.count += 1;
  });
  const rank = (key) => {
    const idx = STATUS_ORDER.indexOf(key);
    return idx === -1 ? STATUS_ORDER.length : idx;
  };
  return { allCount: base.length, tabs: [...tabs.values()].sort((a, b) => rank(a.key) - rank(b.key)) };
};

// Header badge for the chosen board: "20 SME IPOs Tracked · 4 with live GMP"
export const trackedSummary = (rows, board = 'ALL') => {
  const scoped = filterGmpRows(rows, { board });
  const quoted = scoped.filter((row) => row.gmpDisplay).length;
  return { total: scoped.length, quoted, label: `${scoped.length} ${BOARD_LABELS[board] || BOARD_LABELS.ALL} Tracked · ${quoted} with live GMP` };
};
